/**
 * Truthful endings (§10.6–10.8): the downgrade rules for `done` claims, the pure table of what happens when a run
 * ends, the review ladder and escalations. Every escalation opens exactly one `question` proposal and blocks the
 * item with `needs_human{proposalId}`.
 */
import { PLATFORM_TICK_LOST } from "@minga/protocol"
import { levelOf } from "./autonomy"
import { actorTaint, type DecideContext, invalid, Tx } from "./decide/tx"
import { mergeablePrOf, mergedOwnPrOf, reviewedShaOf, runMergeNotice, runMergeOf, short } from "./github"
import { canonicalJson, contentHash, deriveId } from "./hash"
import { itemRecipientOf } from "./multiplayer"
import { isApproveKey, MERGE_KEY, openReviewCards, reviewApproveOptions } from "./outputs"
import { ownerAgentOf } from "./people"
import { RATE_LIMIT_RESUME_SLACK_MS } from "./rate-limit"
import { blockingPredecessors, blockingProposals } from "./readiness"
import { artifactOf, isOpenProposal, isRunEnded, itemOf, leadOf, must, runOf } from "./state"
import { descendantsOf } from "./tree"
import type {
  Actor,
  AgentKind,
  CloseResult,
  Downgrade,
  Evidence,
  Ids,
  Item,
  ItemId,
  OptionEffect,
  ProjectEvent,
  ProjectState,
  Proposal,
  QuestionOption,
  Run,
  RunOutcome,
  StopReason,
  UserId,
} from "./types"
import { runFamilyOf, workerSettingsOf } from "./workers"

type EscalationCause =
  | "auth"
  | "repeated_failure"
  | "non_retryable"
  | "review_rounds_exhausted"
  | "stall"
  | "loop"
  | "spec_conflict"
  | "runner_unavailable"
  | "unreported"

const NON_RETRYABLE: ReadonlySet<StopReason> = new Set([
  "auth_error",
  "policy_refused",
  "permission_denied",
  "sandbox_error",
  "budget_exhausted",
  "mcp_unavailable", // decision 0033: the runner or CLI config has to be fixed by a person
])

export const SYSTEM_ESCALATOR: Actor = { _tag: "system", process: "watchdog" }

/**
 * Loop breakers (decision 0040, amends 0018). A downgraded done claim and a lost runner each get one automatic
 * continuation after this backoff; the next one in a row hands the item back with a card for a person.
 */
export const AUTO_RETRY_BACKOFF_MS = 15_000
/**
 * Stagnation checkpoint: after five attempts without progress, the owner diagnoses and adapts instead of continuing
 * the same worker automatically. At Act this is delegated recovery; lower autonomy asks the person.
 */
export const UNATTENDED_ATTEMPT_CAP = 5
/**
 * Historical budget value, retained for integrations that explicitly choose it. There is no implicit ceiling.
 */
export const AUTO_ATTEMPT_CEILING = 20

/** A person-set worker budget (`attemptCap`, `noProgressCap`) the item reached: the attempts, else null. */
export const workerBudgetReached = (s: ProjectState, item: Item, progressed = false): number | null => {
  const budget = workerSettingsOf(s)
  if (budget.attemptCap != null && (item.autoAttempts ?? 0) >= budget.attemptCap)
    return item.autoAttempts ?? 0
  if (!progressed && budget.noProgressCap != null && (item.unattendedAttempts ?? 0) >= budget.noProgressCap)
    return item.unattendedAttempts ?? 0
  return null
}

/**
 * Wave A §6: did this worker attempt make progress? Its accepted outcome is `progress` (not a downgraded done claim) and
 * it left something new: an artifact posted by this run (artifacts dedupe on item, kind and uri, so a re-posted PR is
 * not new) or a push. Decision 0059: with the runner's head (runner ≥ 0.7.0) a push is "origin/<branch> moved"
 * (`remoteSha ≠ startRemoteSha`), so a repeated `open_pull_request` that pushed nothing new is not progress; without
 * it, Wave A's approximation (`pushedAt`, from the `pr_opened` milestone) stays.
 */
export const madeProgress = (s: ProjectState, run: Run): boolean => {
  if (run.outcome?._tag !== "progress" || run.downgrade !== null) return false
  const posted = s.artifacts.some((a) => a.runId === run.id)
  if (run.head !== undefined)
    return (run.head.remoteSha !== null && run.head.remoteSha !== run.head.startRemoteSha) || posted
  return run.pushedAt !== undefined || posted
}

/** Flow effects a system escalation may offer (§7.1 Actors): flow commands, reassigning, cancelling, new checks. */
export const SYSTEM_EFFECTS: ReadonlySet<OptionEffect["_tag"]> = new Set([
  "RetryItem",
  "UnblockItem",
  "PauseItem",
  "AcceptImpossible",
  "AssignItem",
  "CloseItem", // cancelled only (isSystemEffect)
  "ReviseChecks",
])

/** May a system escalation offer this effect? CloseItem only as a cancellation. */
export const isSystemEffect = (e: OptionEffect): boolean =>
  SYSTEM_EFFECTS.has(e._tag) && !(e._tag === "CloseItem" && e.as === "done")

/**
 * What an agent's question may offer (decision 0032): the system's flow effects plus closing an item as done.
 * Decision 0117: plus `UpdateItem` (a pin to a runner, routing, dates, text). Like every graph effect it is dry-folded
 * under the asker's policy when the card opens and runs as whoever answers it.
 */
export const isAgentQuestionEffect = (e: OptionEffect): boolean =>
  SYSTEM_EFFECTS.has(e._tag) || e._tag === "UpdateItem"

/** The effect tags an agent's question may offer, in words (for the Invalid message). */
export const AGENT_QUESTION_EFFECT_NAMES =
  "RetryItem, UnblockItem, PauseItem, AcceptImpossible, AssignItem, UpdateItem, CloseItem (done or cancelled) or ReviseChecks (a propose_page card: AdoptDocument and SetArtifactState rejected of its proposals/ draft)"

/** Close open escalations about an item that a direct flow command (or a close) made moot. */
export const withdrawEscalations = (tx: Tx, itemId: ItemId): void => {
  for (const p of tx.state.proposals)
    if (isOpenProposal(p) && p.intentKey.startsWith(`escalation:${itemId}:`))
      tx.emit({ _tag: "ProposalWithdrawn", proposalId: p.id })
}

/** Decision 0050: close the item's open human review cards (a cancel made them moot; a refresh replaces them). */
export const withdrawReviewCards = (tx: Tx, itemId: ItemId): void => {
  for (const p of openReviewCards(tx.state, itemId)) tx.emit({ _tag: "ProposalWithdrawn", proposalId: p.id })
}

// ---------------------------------------------------------------------------------------------------------------
// Downgrades

/** Why evidence does not satisfy an item's checks (shared by a worker's done claim and CloseItem{done}). */
export type EvidenceGap =
  | { readonly rule: "evidence_missing"; readonly detail: string } // "no evidence for check pnpm test"
  | { readonly rule: "evidence_unverifiable"; readonly detail: string } // "artifact art_… does not exist"

/** `itemId` is below `item` (at any depth). */
const ownWork = (s: ProjectState, item: Item, itemId: ItemId | null): boolean =>
  itemId !== null && descendantsOf(s, item.id).some((d) => d.id === itemId)

/**
 * Is `evidence` enough for `item`'s checks (decision 0032)? Every `command` check needs a passed `check{name}`
 * evidence with that name; every `artifact` check an artifact of that kind (or a `pull_request` / `commit` evidence
 * for those kinds); a `check` evidence must have passed; an `artifact` evidence must exist. `rubric`, `human` and
 * `metric` checks are the later stages' business. `strict` (CloseItem): an artifact counts for its kind only when it
 * exists, a `check` evidence must name a command check, and the evidence is checked for existence before coverage
 * (the clearer message). A worker's claim (not strict) may also name a check that is not a command check, typically
 * its rubric: that entry is a note for the review stages, not a gap. Otherwise a rubric-only item (every bug, by its
 * type's default check) would downgrade the claim to progress and start a new attempt after every claim, without end
 * (integrator, work-model cutover; decision 0035).
 */
export const checksEvidenceGap = (
  s: ProjectState,
  item: Item,
  evidence: ReadonlyArray<Evidence>,
  opts: { readonly strict: boolean },
): EvidenceGap | null => {
  const checks = item.checks
  const coverage = (): EvidenceGap | null => {
    if (
      item.kind === "publish" &&
      !evidence.some((e) => e._tag === "execution" && e.action.trim() && e.resultRef.trim())
    )
      return {
        rule: "evidence_missing",
        detail:
          "publication needs execution{action, resultRef} evidence of the actual result; a prepared draft or approval is not execution. Verify whether the action already happened before repeating it",
      }
    for (const p of s.proposals)
      if (
        p.body._tag === "outward_action" &&
        p.body.itemId === item.id &&
        p.state === "approved" &&
        !evidence.some((e) => e._tag === "execution" && e.proposalId === p.id && e.revision === p.revision)
      )
        return {
          rule: "evidence_missing",
          detail: `record execution evidence for approved ${p.ref} revision ${p.revision}; approval itself does not execute the call`,
        }
    for (const c of checks) {
      if (c._tag === "command" && !evidence.some((e) => e._tag === "check" && e.name === c.name))
        return {
          rule: "evidence_missing",
          detail: `no evidence for the command check "${c.name}": add check{name: "${c.name}", passed: true}`,
        }
      if (c._tag === "artifact") {
        const has = evidence.some((e) => {
          if (e._tag === "artifact") {
            const a = artifactOf(s, e.artifactId)
            return a?.kind === c.kind || (!opts.strict && a === undefined)
          }
          return (
            (c.kind === "pull_request" && e._tag === "pull_request") ||
            (c.kind === "commit" && e._tag === "commit")
          )
        })
        if (!has)
          return {
            rule: "evidence_missing",
            detail: `no ${c.kind} artifact for the artifact check "${c.description}"`,
          }
      }
    }
    return null
  }
  const verifiable = (): EvidenceGap | null => {
    for (const e of evidence) {
      if (e._tag === "execution" && e.proposalId) {
        const p = s.proposals.find((p) => p.id === e.proposalId)
        if (
          p?.body._tag !== "outward_action" ||
          p.body.itemId !== item.id ||
          p.revision !== e.revision ||
          (p.state !== "approved" && p.state !== "applied")
        )
          return {
            rule: "evidence_unverifiable",
            detail: "execution receipt does not match this item's approved action revision",
          }
      }
      const art = e._tag === "artifact" ? artifactOf(s, e.artifactId) : undefined
      if (e._tag === "artifact" && !art)
        return { rule: "evidence_unverifiable", detail: `artifact ${e.artifactId} does not exist` }
      // a close (strict) cites only the item's own artifacts or those of items below it
      if (opts.strict && art && art.itemId !== item.id && !ownWork(s, item, art.itemId)) {
        const owner = art.itemId ? (itemOf(s, art.itemId)?.ref ?? art.itemId) : "no item"
        return {
          rule: "evidence_unverifiable",
          detail: `artifact ${art.id} belongs to ${owner}, not ${item.ref} or the items below it`,
        }
      }
      if (e._tag === "check" && !e.passed)
        return { rule: "evidence_unverifiable", detail: `the check "${e.name}" did not pass` }
      if (opts.strict && e._tag === "check" && !checks.some((c) => c._tag === "command" && c.name === e.name))
        return {
          rule: "evidence_unverifiable",
          detail: `"${e.name}" is not a command check of ${item.ref}`,
        }
    }
    return null
  }
  return opts.strict ? (verifiable() ?? coverage()) : (coverage() ?? verifiable())
}

const NEXT_MAX = 1_000
const cutNext = (x: string): string => (x.length > NEXT_MAX ? `${x.slice(0, NEXT_MAX - 1)}…` : x)

/** One piece of done evidence in words, for a handoff: a URL, a check, a commit, an artifact or run id. */
const evidenceWords = (e: Evidence): string => {
  switch (e._tag) {
    case "execution":
      return `${e.action}: ${e.resultRef}`
    case "pull_request":
      return `${e.url} (head ${e.headSha.slice(0, 7)})`
    case "url":
      return e.url
    case "check":
      return `check ${e.name} ${e.passed ? "passed" : "failed"}`
    case "commit":
      return `commit ${e.sha.slice(0, 7)}`
    case "artifact":
      return `artifact ${e.artifactId}`
    case "run":
      return `run ${e.runId}`
    default:
      return ""
  }
}

/**
 * Downgrades apply only to `done` (§10.6): not completed → progress (not_completed); no evidence for an
 * automatable check (evidence_missing); evidence pointing at missing artifacts or a failed check
 * (evidence_unverifiable). A `check` naming no command check is a note, not a downgrade (see `checksEvidenceGap`).
 * Other claims are kept as reported.
 */
export const acceptOutcome = (
  s: ProjectState,
  run: Run,
  stopReason: StopReason,
): { readonly accepted: RunOutcome; readonly downgrade: Downgrade | null } => {
  const claim: RunOutcome = run.claimed ?? { _tag: "unreported" }
  if (claim._tag !== "done") return { accepted: claim, downgrade: null }
  // the detail travels into the next attempt's handoff (`next`) and onto the run row (decision 0040)
  const toProgress = (rule: Downgrade["rule"], detail: string) => ({
    accepted: {
      _tag: "progress" as const,
      summary: claim.summary,
      next: `done claim not accepted: ${detail}`,
    },
    downgrade: { from: "done" as const, rule, detail },
  })
  if (stopReason === "runner_lost") {
    // decision 0070 (TRI-26): a lost runner never reported its final head, so the claim can't count; the next attempt
    // is told to verify the claimed work and report it, not to redo it
    const r = toProgress("not_completed", `the run stopped with ${stopReason} before the claim could count`)
    const ev = claim.evidence.map(evidenceWords).filter((x) => x !== "")
    const what = [claim.summary.trim(), ev.length > 0 ? `evidence: ${ev.join(", ")}` : ""]
      .filter((x) => x !== "")
      .join("; ")
    return {
      ...r,
      accepted: {
        ...r.accepted,
        next: cutNext(
          `The previous attempt claimed done${what ? ` (${what})` : ""} before its runner was lost. Verify that work and report it; don't redo it.`,
        ),
      },
    }
  }
  if (stopReason !== "completed")
    return toProgress("not_completed", `the run stopped with ${stopReason} before the claim could count`)
  const item = run.itemId ? itemOf(s, run.itemId) : undefined
  if (item) {
    const gap = checksEvidenceGap(s, item, claim.evidence, { strict: false })
    if (gap) return toProgress(gap.rule, gap.detail)
  }
  return { accepted: claim, downgrade: null }
}

// ---------------------------------------------------------------------------------------------------------------
// Escalations and the review ladder (emitting into a transaction)

const option = (
  key: string,
  label: string,
  effects: ReadonlyArray<OptionEffect>,
  detail: string | null = null,
): QuestionOption => ({ key, label, detail, effects })

/** The usual flow options of an escalation about an item. */
export const escalationOptions = (item: Item, cause: EscalationCause): ReadonlyArray<QuestionOption> => {
  const retry = option("retry", cause === "auth" ? "I fixed it: retry" : "Retry", [
    { _tag: "RetryItem", itemId: item.id },
  ])
  const pause = option("pause", "Pause the item", [{ _tag: "PauseItem", itemId: item.id, paused: true }])
  const cancel = option("cancel", "Cancel the item", [
    {
      _tag: "CloseItem",
      itemId: item.id,
      as: "cancelled",
      resolution: { _tag: "wont_do" },
      note: `cancelled after escalation (${cause})`,
    },
  ])
  return [retry, pause, cancel]
}

/** Open a proposal record (shared by every kind; the caller supplies the body and routing). */
export const openProposal = (
  tx: Tx,
  p: Omit<
    Proposal,
    | "ref"
    | "contentHash"
    | "revision"
    | "state"
    | "perChange"
    | "resolution"
    | "contentSeq"
    | keyof ReturnType<Tx["recordMeta"]>
  > & {
    readonly ownerId: Ids["UserId"]
  },
): Proposal => {
  const proposal: Proposal = {
    ...tx.recordMeta(),
    contentSeq: (tx.state.seq + 1) as Ids["Seq"],
    ...p,
    ref: tx.nextRef("P"),
    revision: 1,
    contentHash: contentHash(p.body),
    state: "open",
    perChange: p.body._tag === "plan_change" ? p.body.changes.map(() => "pending" as const) : [],
    resolution: null,
  }
  tx.emit({ _tag: "ProposalOpened", proposal })
  return proposal
}

/**
 * Who an escalation or review about an item goes to: its review's escalateTo, else the item's recipient (decision 0127:
 * the person responsible for it, else its asker, else the project's admins; never by default the agent's keeper).
 */
export const escalationTarget = (s: ProjectState, item: Item): UserId =>
  must(item.review.escalateTo ?? itemRecipientOf(s, item) ?? s.project?.ownerId, "escalation target")

/** The escalation card about an item: one question proposal to a person, blocking the item (Retry · Pause · Cancel). */
const openEscalationCard = (
  tx: Tx,
  item: Item,
  cause: EscalationCause,
  detail: string,
  runId: Ids["RunId"] | null,
): Ids["ProposalId"] => {
  const proposalId = deriveId<Ids["ProposalId"]>(
    "prop",
    tx.now,
    `escalation:${item.id}:${runId ?? "-"}:${cause}:${tx.state.seq}`,
  )
  const cur = itemOf(tx.state, item.id) ?? item
  const live = cur.status === "in_progress" || cur.status === "in_review"
  const to = escalationTarget(tx.state, cur)
  // decision 0093: one escalation card per item. A new stop on an item whose card is still open updates that card (the
  // newest cause and detail, the same ref) instead of adding a second one
  const open = tx.state.proposals.find(
    (p) =>
      isOpenProposal(p) && p.intentKey.startsWith(`escalation:${item.id}:`) && p.body._tag === "question",
  )
  if (open?.body._tag === "question") {
    const body = {
      ...open.body,
      prompt: `${item.ref} ${item.title}: ${detail}`,
      options: escalationOptions(item, cause),
      escalation: { cause, detail, ...(runId ? { runId } : {}) },
    }
    tx.emit({
      _tag: "ProposalRevised",
      proposalId: open.id,
      revision: open.revision + 1,
      contentHash: contentHash(body),
      body,
      reasoning: detail,
    })
    return open.id
  }
  openProposal(tx, {
    id: proposalId,
    ownerId: to,
    body: {
      _tag: "question",
      prompt: `${item.ref} ${item.title}: ${detail}`,
      subject: { _tag: "item", id: item.id },
      options: escalationOptions(item, cause),
      allowFreeText: true,
      defaultOnExpiry: null,
      answersAsk: null,
      escalation: { cause, detail, ...(runId ? { runId } : {}) },
    },
    proposedBy: SYSTEM_ESCALATOR,
    reasoning: detail,
    oneLine: `${item.ref} needs you: ${cause.replace(/_/g, " ")}`,
    risk: "medium",
    reversibility: "reversible",
    tier: live ? "blocking" : "soon",
    blocks: [{ _tag: "item", id: item.id }],
    tainted: false,
    approver: { _tag: "human", userId: to },
    decideBy: null,
    expiresAt: null,
    intentKey: `escalation:${item.id}:${cause}:${tx.state.seq}`,
  })
  return proposalId
}

/** Decision 0154: the intent key of an item's go card (one per item). */
export const goIntentKey = (itemId: ItemId): string => `go:${itemId}`

/**
 * Decision 0154 (review 10-23 #4: REP-91 spent real money on its own once it became ready): hold an item for a person's
 * go. A gate card (decision 0041) to that person that `blocks` the item, so readiness holds it and nothing starts it:
 * "Go ahead" (`approve`) releases it, "Not yet" pauses it as theirs (only they resume it). The card goes to the person
 * directly, never up an agent chain: no agent can give a person's go. One open card per item.
 */
export const openGoGate = (
  tx: Tx,
  item: Item,
  go: { readonly userId: UserId; readonly why?: string | undefined },
): void => {
  const m = tx.state.members.find((x) => x.userId === go.userId)
  if (tx.state.members.length > 0 && (!m || m.role === "viewer"))
    invalid("goFrom names a working member of the project (an @handle)", "goFrom")
  const intentKey = goIntentKey(item.id)
  if (tx.state.proposals.some((p) => p.intentKey === intentKey && isOpenProposal(p))) return
  const why = (go.why ?? "").trim() || item.why.trim()
  const name = m?.displayName.trim() || (m ? `@${m.handle}` : "the person")
  openProposal(tx, {
    id: deriveId<Ids["ProposalId"]>("prop", tx.now, `${intentKey}:${tx.state.seq}`),
    ownerId: go.userId,
    body: {
      _tag: "question",
      prompt: `Go ahead with ${item.ref} ${item.title}? It waits for ${name}'s go before anything starts${why ? `: ${why}` : "."}`,
      subject: { _tag: "item", id: item.id },
      options: [
        option("approve", "Go ahead", []),
        {
          ...option("not_yet", "Not yet", []),
          detail: "It stays paused until you resume it",
          releases: false,
        },
      ],
      allowFreeText: true,
      defaultOnExpiry: null,
      answersAsk: null,
      escalation: null,
    },
    proposedBy: tx.actor,
    reasoning: why,
    oneLine: `${item.ref} waits for your go: ${item.title}`,
    risk: "medium",
    reversibility: "irreversible",
    tier: "soon",
    blocks: [{ _tag: "item", id: item.id }],
    tainted: actorTaint(tx.state, tx.actor) === "tainted",
    approver: { _tag: "human", userId: go.userId },
    decideBy: null,
    expiresAt: null,
    intentKey,
  })
}

/** One question proposal to a person + blocked{needs_human} on the item: only a person continues. */
export const escalate = (
  tx: Tx,
  item: Item,
  cause: EscalationCause,
  detail: string,
  runId: Ids["RunId"] | null,
): Ids["ProposalId"] => {
  const proposalId = openEscalationCard(tx, item, cause, detail, runId)
  const cur = must(itemOf(tx.state, item.id), "item")
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId: item.id,
    from: cur.status,
    to: "blocked",
    cause: { _tag: "blocked" },
    blocked: { _tag: "needs_human", proposalId },
    resolution: null,
  })
  return proposalId
}

export interface HumanReviewOptions {
  /** A sentence on top of the prompt (a refused merge, decision 0059). */
  readonly notice?: string
  /** Bind a card that offers Merge to the PR's current head (the person was told it moved), not the run's. */
  readonly bindToHead?: boolean
  /** Bind a card that offers Merge to this SHA (a re-opened card after a refused merge: the order's reviewed SHA). */
  readonly bind?: { readonly sha: string; readonly source: "run_head" | "evidence" | "snapshot" }
}

/**
 * Decision 0147 (lesson 4): every human check of an item reaches its one review card. One check: its prompt as
 * written; several: one line each, naming the person a check names when it is not the card's reviewer.
 */
export const humanChecksText = (s: ProjectState, item: Item, approverId: UserId): string | null => {
  const checks = item.checks.filter((c) => c._tag === "human")
  if (checks.length === 0) return null
  if (checks.length === 1) return checks[0]?.prompt ?? null
  const nameOf = (u: UserId) => s.members.find((m) => m.userId === u)?.displayName ?? "another person"
  return [
    `${checks.length} checks for a person:`,
    ...checks.map(
      (c) =>
        `- ${c.prompt.trim().replace(/\n+/g, " ")}${c.approverId && c.approverId !== approverId ? ` (${nameOf(c.approverId)}'s check)` : ""}`,
    ),
  ].join("\n")
}

/** The human-stage question for the current round (approve · request changes · cancel). */
export const openHumanReview = (tx: Tx, item: Item, opts: HumanReviewOptions = {}): void => {
  const approverId = humanReviewerOf(tx.state, item)
  const checksText = humanChecksText(tx.state, item, approverId)
  const outward = item.kind === "publish"
  const base = outward
    ? `Review ${item.ref} ${item.title}. Approval reviews the work; completion requires evidence of execution.${checksText ? `\n\n${checksText}` : ""}`
    : (checksText ?? `Review ${item.ref} ${item.title}`)
  // decision 0059: a run's merge asks to confirm (state-driven); a card offering Merge is bound to the reviewed SHA
  const ranMerge = runMergeOf(tx.state, item)
  const options = reviewApproveOptions(tx.state, item)
  const mergePr = options.some((o) => o.key === MERGE_KEY) ? mergeablePrOf(tx.state, item) : undefined
  const bound = mergePr
    ? opts.bind
      ? opts.bind
      : opts.bindToHead && mergePr.pr
        ? { sha: mergePr.pr.snapshot.headSha, source: "snapshot" as const }
        : reviewedShaOf(tx.state, mergePr)
    : null
  const notices = [opts.notice, ranMerge ? runMergeNotice(ranMerge, tx.state) : undefined].filter(
    (x): x is string => x !== undefined && x.trim() !== "",
  )
  const prompt = [...notices, base, ...(bound ? [`Reviewing ${short(bound.sha)}.`] : [])].join("\n\n")
  const proposal = openProposal(tx, {
    id: deriveId<Ids["ProposalId"]>("prop", tx.now, `review:${item.id}:${item.review.round}:${tx.state.seq}`),
    ownerId: approverId,
    body: {
      _tag: "question",
      prompt,
      subject: { _tag: "item", id: item.id },
      // decision 0050: picks of the item's live outputs; every card keeps an option with the key "approve"
      options: [
        ...options,
        option("changes", "Request changes", []),
        option("cancel", "Cancel the item", [
          {
            _tag: "CloseItem",
            itemId: item.id,
            as: "cancelled",
            resolution: { _tag: "wont_do" },
            note: "cancelled at human review",
          },
        ]),
      ],
      allowFreeText: true,
      defaultOnExpiry: null,
      answersAsk: null,
      escalation: null,
    },
    proposedBy: { _tag: "system", process: "scheduler" },
    reasoning: `Round ${item.review.round}: a person reviews ${item.ref}.`,
    oneLine: `Review ${item.ref}: ${item.title}`,
    risk: "low",
    reversibility: "reversible",
    tier: "soon",
    blocks: [],
    tainted: false,
    approver: { _tag: "human", userId: approverId },
    decideBy: null,
    expiresAt: null,
    intentKey: reviewIntentKey(item.id, item.review.round),
  })
  if (mergePr && bound)
    tx.emit({
      _tag: "PrReviewBound",
      artifactId: mergePr.id,
      itemId: item.id,
      proposalId: proposal.id,
      sha: bound.sha,
      source: bound.source,
    })
}

export const reviewIntentKey = (itemId: ItemId, round: number): string => `review:${itemId}:${round}`

const approveOptionsOf = (p: Proposal): ReadonlyArray<QuestionOption> =>
  p.body._tag === "question" ? p.body.options.filter((o) => isApproveKey(o.key) || o.key === MERGE_KEY) : []

/**
 * Decision 0050: the card always matches the live outputs. When the item is in review at the human stage and its open
 * card's approve options (key, label, effects) differ from what the outputs give now, the card is withdrawn and opened
 * again (a new proposal id, the same intentKey). Called after an artifact is posted on the item.
 */
export const refreshHumanReview = (tx: Tx, itemId: ItemId): void => {
  const t = itemOf(tx.state, itemId)
  if (t?.status !== "in_review" || t.review.stages[t.review.stage] !== "human") return
  const key = reviewIntentKey(t.id, t.review.round)
  const cards = openReviewCards(tx.state, t.id).filter((p) => p.intentKey === key)
  const [card] = cards
  if (!card) return
  const want = canonicalJson(reviewApproveOptions(tx.state, t))
  if (cards.length === 1 && canonicalJson(approveOptionsOf(card)) === want) return
  withdrawReviewCards(tx, t.id)
  openHumanReview(tx, must(itemOf(tx.state, t.id), "item"))
}

type StageName = Item["review"]["stages"][number]

/**
 * Decision 0120: the review stage an item waits at. `past_end`: its index points past its stages (a ChecksRevised before
 * 0120 shortened them and kept the old index); the owner's (or the lead's) review then completes it, as at a last agent
 * stage.
 */
export const reviewStageOf = (t: Item): StageName | "past_end" =>
  t.review.stages[t.review.stage] ?? "past_end"

/**
 * Decision 0120: where an item's review index lands when its stages are replaced: past the stages it already passed
 * (each passed stage counts once, in the new order). `stages.length` means none is left ahead.
 */
export const remapReviewStage = (from: Item["review"], stages: ReadonlyArray<StageName>): number => {
  const passed: StageName[] = from.stages.slice(0, from.stage)
  let j = 0
  while (j < stages.length) {
    const k = passed.indexOf(stages[j] as StageName)
    if (k < 0) break
    passed.splice(k, 1)
    j++
  }
  return j
}

/**
 * Decision 0120 (amends 0094): the one next step for an item in review, by the stage it waits at, naming the tool. Every
 * in_review refusal (review, close_item, retry_item, unblock_item, a plan MoveItem) says this, and never points to a
 * tool that would refuse too.
 */
export const inReviewNextStep = (s: ProjectState, t: Item): string => {
  const stage = reviewStageOf(t)
  switch (stage) {
    case "validator": {
      const live = s.runs.filter((r) => r.kind === "validator" && r.itemId === t.id && !isRunEnded(r)).at(-1)
      const skip = "; to skip it, plan ReviseChecks with review.stages without validator"
      if (!live)
        return `${t.ref} waits for its validator, which starts when a runner can take it: wait for its verdict${skip}`
      const how = live.state._tag === "leased" ? "running" : "queued"
      return `${t.ref} waits for its validator ${live.ref} (${how}): wait for its verdict, or stop_run ${live.ref} if it is going the wrong way${skip}`
    }
    case "agent":
      return `${t.ref} waits for its agent review (round ${t.review.round}): the owner or the lead approves it or requests changes with review`
    case "human": {
      const card = openReviewCards(s, t.id).find((p) => p.intentKey === reviewIntentKey(t.id, t.review.round))
      return card
        ? `${t.ref} waits for a person's review on ${card.ref}: they answer it in the app; resolve_card applies an answer they gave you in chat`
        : `${t.ref} waits for a person's review: ask_human if you need their answer`
    }
    case "past_end":
      return `${t.ref} has passed every review stage it has now: the owner or the lead completes it with review (approve)`
  }
}

/** Side effects of entering the current stage (agent: a wake via reactions; validator: a dispatch; human: a question). */
export const enterCurrentStage = (tx: Tx, itemId: ItemId): void => {
  const t = itemOf(tx.state, itemId)
  if (t?.status !== "in_review") return
  if (t.review.stages[t.review.stage] !== "human") return
  openHumanReview(tx, t)
}

/** The evidence a completed item carries: a close's evidence, else the accepted `done` evidence of its last run. */
export const doneEvidence = (s: ProjectState, t: Item): ReadonlyArray<Evidence> => {
  if (t.closeEvidence?.length) return t.closeEvidence
  const r = t.lastRunId ? runOf(s, t.lastRunId) : undefined
  return r?.outcome?._tag === "done" ? r.outcome.evidence : []
}

/** The current stage passed: next stage, or `done` after the last (§10.8). */
export const passStage = (tx: Tx, itemId: ItemId, verdict: "approve" | "pass"): void => {
  const t = must(itemOf(tx.state, itemId), "item")
  const stage = t.review.stages[t.review.stage] ?? "agent"
  if (t.review.stage + 1 < t.review.stages.length) {
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId,
      from: "in_review",
      to: "in_review",
      cause: { _tag: "review", round: t.review.round, stage, verdict },
      blocked: null,
      resolution: null,
    })
    enterCurrentStage(tx, itemId)
  } else {
    // Old draft-only runs/cards remain reviewable. Approval never fabricates execution.
    if (
      t.kind === "publish" &&
      checksEvidenceGap(tx.state, t, doneEvidence(tx.state, t), { strict: false })
    ) {
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId,
        from: "in_review",
        to: "todo",
        cause: { _tag: "planned" },
        blocked: null,
        resolution: null,
      })
      return
    }
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId,
      from: "in_review",
      to: "done",
      cause: { _tag: "completed", evidence: doneEvidence(tx.state, t) },
      blocked: null,
      resolution: null,
    })
  }
}

/** Stages from the current one on that got no verdict (a merge skipped them; the human one when it was answered). */
const skippedStages = (t: Item, humanAnswered: boolean): ReadonlyArray<Item["review"]["stages"][number]> =>
  t.review.stages.slice(t.review.stage).filter((st) => !(st === "human" && humanAnswered))

/** A validator run of the item that has not been leased yet is cancelled: the code has landed. */
const cancelPendingValidators = (tx: Tx, itemId: ItemId, reason: string): void => {
  for (const r of tx.state.runs)
    if (
      r.kind === "validator" &&
      r.itemId === itemId &&
      (r.state._tag === "requested" || r.state._tag === "waiting")
    )
      tx.emit({ _tag: "RunCancelRequested", runId: r.id, reason })
}

export interface MergedFacts {
  readonly url: string
  readonly number: number
  readonly sha: string | null
  readonly login: string | null
  readonly via: "github" | "minga"
  /** `minga`: the person who chose Merge. */
  readonly by?: UserId
}

/**
 * Decision 0059: a person merged the item's PR (on GitHub, or with Merge in Minga) while it was in review. The open
 * human card of this round is answered `merge` (by the person who chose Merge, or as GitHub's with a note), any other
 * review card is withdrawn, the human stage gets its approve review when it is the current stage (no review is written
 * for an agent or validator stage: nobody gave a verdict), a pending validator run is cancelled, and the item is done
 * with `cause.merged{…, skipped}`: the code has landed.
 */
export const completeByMerge = (tx: Tx, itemId: ItemId, merged: MergedFacts): void => {
  const t = must(itemOf(tx.state, itemId), "item")
  if (t.status !== "in_review") return
  const stage = t.review.stages[t.review.stage]
  const note =
    merged.via === "minga"
      ? `merged ${short(merged.sha)}`
      : `merged on GitHub by ${merged.login ?? "someone"} (${short(merged.sha)})`
  const key = reviewIntentKey(t.id, t.review.round)
  let answered = false
  for (const p of openReviewCards(tx.state, t.id)) {
    if (!answered && p.intentKey === key) {
      tx.emit({
        _tag: "ProposalAnswered",
        proposalId: p.id,
        revision: p.revision,
        optionKey: MERGE_KEY,
        text: null,
        ...(merged.via === "minga" && merged.by !== undefined
          ? { by: { _tag: "user" as const, userId: merged.by } }
          : {}),
        note,
      })
      answered = true
    } else tx.emit({ _tag: "ProposalWithdrawn", proposalId: p.id })
  }
  if (stage === "human")
    tx.emit({
      _tag: "ReviewRecorded",
      itemId: t.id,
      round: t.review.round,
      stage: "human",
      verdict: "approve",
      notes: note,
      findings: [],
      runId: null,
    })
  cancelPendingValidators(tx, t.id, "merged on GitHub")
  const skipped = skippedStages(t, stage === "human")
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId: t.id,
    from: "in_review",
    to: "done",
    cause: {
      _tag: "merged",
      url: merged.url,
      number: merged.number,
      sha: merged.sha,
      login: merged.login,
      via: merged.via,
      ...(merged.by !== undefined ? { by: merged.by } : {}),
      ...(skipped.length > 0 ? { skipped } : {}),
    },
    blocked: null,
    resolution: null,
  })
}

/**
 * Decision 0156 (REP-66): a merge ends the review loop. The item's own PR is merged, and the item is open outside a
 * running attempt (in review, blocked after its review rounds, back in todo for a fix, or a validator round about to
 * start): it is done with `cause.merged`, its unreviewed stages named as skipped, every live validator run of it
 * cancelled (a merged head is not reviewed again) and its review cards withdrawn. Never when a person's review stage is
 * still ahead (a merge by a run waits for them, decision 0059): returns false and changes nothing. The majors a failing
 * verdict left are the lead's to schedule (0154's merged-over line).
 */
export const closeByMerge = (tx: Tx, itemId: ItemId, except: Ids["RunId"] | null = null): boolean => {
  const t = itemOf(tx.state, itemId)
  if (!t || !(t.status === "in_review" || t.status === "blocked" || t.status === "todo")) return false
  if (t.kind === "publish") return false
  const a = mergedOwnPrOf(tx.state, t)
  const snap = a?.pr?.snapshot
  if (!a || !snap) return false
  if (t.review.stages.indexOf("human", t.review.stage) >= 0) return false
  // an attempt under way keeps going: its own end decides
  if (tx.state.runs.some((r) => r.kind === "worker" && r.itemId === t.id && !isRunEnded(r))) return false
  for (const r of tx.state.runs)
    if (
      r.kind === "validator" &&
      r.itemId === t.id &&
      r.id !== except &&
      !isRunEnded(r) &&
      r.cancelRequestedAt === undefined
    )
      tx.emit({
        _tag: "RunCancelRequested",
        runId: r.id,
        reason: `${t.ref}'s PR #${snap.number} is merged`,
        requestedAt: tx.now,
      })
  withdrawReviewCards(tx, t.id)
  // a round that just gave its verdict was not skipped; the stages after it were
  const skipped = except !== null ? t.review.stages.slice(t.review.stage + 1) : skippedStages(t, false)
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId: t.id,
    from: t.status,
    to: "done",
    cause: {
      _tag: "merged",
      url: snap.url,
      number: snap.number,
      sha: snap.mergeSha,
      login: snap.mergedBy,
      via: a.pr?.mergedVia ?? "github",
      ...(skipped.length > 0 ? { skipped } : {}),
    },
    blocked: null,
    resolution: null,
  })
  return true
}

/**
 * Decision 0101: whose close is this? A person's own command, or a change a person approved on a card (an agent's plan
 * card, or an answer `resolve_card` applied as them).
 */
export const personsCloseBy = (tx: Tx): UserId | null => {
  if (tx.actor._tag === "user") return tx.actor.userId
  const by = tx.scope.approvedBy?.by
  return by?._tag === "user" ? by.userId : null
}

/** The person who reviews an item's human stage: its human check's approver, else its accountable person. */
export const humanReviewerOf = (s: ProjectState, item: Item): UserId => {
  const humanCheck = item.checks.find((c) => c._tag === "human")
  return (humanCheck?._tag === "human" ? humanCheck.approverId : null) ?? escalationTarget(s, item)
}

/**
 * Decision 0101 (S7): a close a person made or approved ends the item. Their approval is the final review: the
 * automated review stages (validator, agent) are skipped without a verdict and named in the cause, every validator run of
 * the item that has not ended is cancelled (a queued one never overrides the person, P-105), and open escalation and
 * review cards are withdrawn. The item is `done`, unless it has a human stage another person reviews: then it waits at
 * that stage (their card opens).
 */
export const closeByPerson = (
  tx: Tx,
  t: Item,
  person: UserId,
  note: string,
  evidence: ReadonlyArray<Evidence>,
  result: CloseResult | undefined,
): void => {
  const stages = t.review.stages
  const humanAt = stages.indexOf("human")
  const waitsForReviewer = humanAt >= 0 && humanReviewerOf(tx.state, t) !== person
  const skipped = (waitsForReviewer ? stages.slice(0, humanAt) : stages).filter((st) => st !== "human")
  withdrawEscalations(tx, t.id)
  withdrawReviewCards(tx, t.id)
  for (const r of tx.state.runs)
    if (r.kind === "validator" && r.itemId === t.id && !isRunEnded(r) && r.cancelRequestedAt === undefined)
      tx.emit({
        _tag: "RunCancelRequested",
        runId: r.id,
        reason: `${t.ref} was closed by a person`,
        requestedAt: tx.now,
      })
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId: t.id,
    from: t.status,
    to: waitsForReviewer ? "in_review" : "done",
    cause: {
      _tag: "closed",
      note,
      evidence,
      ...(result ? { result } : {}),
      ...(skipped.length > 0 ? { skipped } : {}),
    },
    blocked: null,
    resolution: null,
  })
  if (waitsForReviewer) enterCurrentStage(tx, t.id)
}

/**
 * Decision 0059: a Minga run merged the PR with the person's gh while the item was in review. That is not a person's
 * review: an item with a human stage moves to it (earlier stages skipped without a verdict, a pending validator
 * cancelled) and its card opens or re-opens asking to confirm; an item with no human stage is done.
 */
export const advanceToHumanByMerge = (
  tx: Tx,
  itemId: ItemId,
  merged: {
    readonly url: string
    readonly number: number
    readonly sha: string | null
    readonly login: string | null
  },
): void => {
  const t = must(itemOf(tx.state, itemId), "item")
  if (t.status !== "in_review") return
  const cause = (skipped: ReadonlyArray<Item["review"]["stages"][number]>) => ({
    _tag: "merged" as const,
    url: merged.url,
    number: merged.number,
    sha: merged.sha,
    login: merged.login,
    via: "run" as const,
    ...(skipped.length > 0 ? { skipped } : {}),
  })
  const humanAt = t.review.stages.indexOf("human", t.review.stage)
  cancelPendingValidators(tx, t.id, "merged by a Minga run")
  if (humanAt < 0) {
    withdrawReviewCards(tx, t.id)
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId,
      from: "in_review",
      to: "done",
      cause: cause(skippedStages(t, false)),
      blocked: null,
      resolution: null,
    })
    return
  }
  if (humanAt === t.review.stage) {
    withdrawReviewCards(tx, t.id)
    openHumanReview(tx, t)
    return
  }
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId,
    from: "in_review",
    to: "in_review",
    cause: cause(t.review.stages.slice(t.review.stage, humanAt)),
    blocked: null,
    resolution: null,
  })
  withdrawReviewCards(tx, t.id)
  enterCurrentStage(tx, itemId)
}

/** At most this much of the findings goes into the exhausted-rounds blocker. */
const FINDINGS_MAX = 300

/** The findings an exhausted round names: a clean validator run's first two, else the reviewer's notes. */
const roundFindings = (s: ProjectState, runId: Ids["RunId"] | null, notes: string | null): string => {
  const r = runId ? runOf(s, runId) : undefined
  if (r?.kind === "validator") {
    // text from a tainted run is not inlined: the blocker is rendered as Minga's words (decision 0040)
    if (r.taint === "tainted" || r.verdict?._tag !== "fail") return ""
    const text = r.verdict.findings
      .slice(0, 2)
      .map((f) => f.text.trim())
      .join("; ")
    return text.length > FINDINGS_MAX ? `${text.slice(0, FINDINGS_MAX - 1)}…` : text
  }
  const n = (notes ?? "").trim()
  return n.length > FINDINGS_MAX ? `${n.slice(0, FINDINGS_MAX - 1)}…` : n
}

/**
 * Changes requested / validator fail: back to todo (findings go into the handoff). Past maxRounds (Wave A §4.6, amends
 * decision 0032) the item goes back to its owner agent, blocked with a reported blocker whose run is the last WORKER
 * run (so the owner's retry_item starts a new worker attempt, not the validator). A selected worker budget can
 * escalate to a person; there is no default review-cycle count gate.
 */
export const failStage = (
  tx: Tx,
  itemId: ItemId,
  verdict: "changes_requested" | "fail",
  runId: Ids["RunId"] | null,
  notes: string | null = null,
): void => {
  const t = must(itemOf(tx.state, itemId), "item")
  const stage = t.review.stages[t.review.stage] ?? "agent"
  // decision 0156: a failing round on an item whose PR is already merged ends the loop (done, merged), never a fix
  // round or "rounds exhausted" (REP-66)
  if (stage !== "human" && closeByMerge(tx, itemId, runId)) return
  // decision 0050: a person may iterate as often as they like; their rounds are not counted toward maxRounds
  const counted = t.review.round - (t.review.personRounds ?? 0)
  if (stage !== "human" && counted >= t.review.maxRounds) {
    // A person-selected count budget holds further attempts.
    const limit = workerBudgetReached(tx.state, t)
    if (limit !== null) {
      escalate(
        tx,
        t,
        "loop",
        `${limit} attempts since a person last looked; the configured worker budget was reached after ${counted} review rounds`,
        runId,
      )
      return
    }
    const findings = roundFindings(tx.state, runId, notes)
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId,
      from: "in_review",
      to: "blocked",
      cause: { _tag: "review", round: t.review.round, stage, verdict },
      blocked: {
        _tag: "reported",
        blocker: {
          _tag: "other",
          what: `review rounds exhausted after ${counted} rounds${findings ? `: ${findings}` : ""}. You decide: close it with follow-ups for what is left, revise the checks, split it, switch the model (retry_item with a route), or run one more round (retry_item). ask_human only for what needs the person's own authority.`,
        },
        runId: t.lastRunId,
      },
      resolution: null,
    })
    return
  }
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId,
    from: "in_review",
    to: "todo",
    cause: { _tag: "review", round: t.review.round, stage, verdict },
    blocked: null,
    resolution: null,
  })
}

/**
 * Validator inconclusive. Decision 0134 (lesson 4): it goes to the item's agent (its owner, else the lead), blocked at
 * the validator stage with the validator's reason, like a validator that stopped: retry_item re-runs it (elsewhere with
 * a route), revise_checks or a close are the agent's call, and it asks a person only for their own authority. Only
 * when no agent answers for the item is a human stage added for this round (evolve inserts it) and its question opened.
 */
export const inconclusiveStage = (
  tx: Tx,
  itemId: ItemId,
  runId: Ids["RunId"] | null = null,
  why = "",
): void => {
  const t = must(itemOf(tx.state, itemId), "item")
  if (runId !== null && failureHandlerOf(tx.state, t)) {
    const vr = runOf(tx.state, runId)
    const ref = vr?.ref ?? "the validator"
    // text from a tainted run is not inlined: the blocker is rendered as Minga's words (decision 0040, as roundFindings)
    const text = vr?.taint === "tainted" ? "" : why.trim()
    const reason = text.length > 300 ? `${text.slice(0, 299)}…` : text
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId,
      from: "in_review",
      to: "blocked",
      cause: { _tag: "blocked" },
      blocked: {
        _tag: "reported",
        blocker: {
          _tag: "other",
          what: `the validator ${ref} could not decide${reason ? `: ${reason}` : ""}. You decide: retry_item re-runs it (with a validator route elsewhere if its place was the problem), revise the checks, close it, or unblock_item to send it back to a worker. ask_human only for what needs the person's own authority.`,
        },
        runId,
      },
      resolution: null,
    })
    return
  }
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId,
    from: "in_review",
    to: "in_review",
    cause: { _tag: "review", round: t.review.round, stage: "validator", verdict: "inconclusive" },
    blocked: null,
    resolution: null,
  })
  enterCurrentStage(tx, itemId)
}

// ---------------------------------------------------------------------------------------------------------------
// When a run ends

/**
 * What follows a run's end. Rev 2.1 (decision 0018): a failed run goes back to its owner. Decision 0040 adds bounded
 * automatic continuations: `new_attempt` is the continuation after a `progress` outcome, after the first downgraded done
 * claim in a row (after AUTO_RETRY_BACKOFF_MS) and after the first lost runner in a row (the same backoff); the item is
 * back in `todo` with `retry.at` and the scheduler dispatches it like any ready item once the backoff has passed.
 */
export type RetryDecision =
  | { readonly _tag: "none" }
  | { readonly _tag: "new_attempt"; readonly notBefore: number; readonly sameProfile: boolean }
  | { readonly _tag: "escalate"; readonly cause: string }

export interface OutcomeDecision {
  readonly accepted: RunOutcome
  readonly downgrade: Downgrade | null
  readonly itemEvents: ReadonlyArray<ProjectEvent>
  readonly retry: RetryDecision
}

export interface RunEndOptions {
  /**
   * Provider reset time for rate_limited (decision 0155: the hub's `RunEnded.rateLimit`, from the provider's words or
   * the profile's quota). A worker continues by itself at it.
   */
  readonly resetsAt?: number | null
  /** Decision 0155: which limit ran out (`5h`, `24h`, `7d`), when known. */
  readonly window?: "5h" | "24h" | "7d"
}

type RetryAt = { readonly at: number; readonly pinProfile: null }

/**
 * Hand an item back to its owner with a card for a person (decision 0040): the attempt ends `blocked{reported}` (the
 * owner wakes with `blocker`, and its retry_item still works) and an escalation question with Retry · Pause · Cancel
 * opens, so the stop is never silent in Needs-you. A retry, unblock or close by anyone withdraws the card.
 */
export const handBackWithCard = (
  tx: Tx,
  item: Item,
  run: Run,
  stopReason: StopReason,
  outcome: RunOutcome["_tag"],
  cause: EscalationCause,
  detail: string,
): RetryDecision => {
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId: item.id,
    from: "in_progress",
    to: "blocked",
    cause: { _tag: "attempt_ended", runId: run.id, stopReason, outcome, retry: null },
    blocked: { _tag: "reported", blocker: { _tag: "other", what: detail }, runId: run.id },
    resolution: null,
  })
  openEscalationCard(tx, must(itemOf(tx.state, item.id), "item"), cause, detail, run.id)
  return { _tag: "escalate", cause }
}

/**
 * Item transitions after a worker or validator run ended (the table in §10.7, as amended by rev 2.1 and decision
 * 0040). Runs inside a transaction whose state already has the RunEnded applied.
 *
 * Rev 2.1 (decision 0018): a failed run (cli_error, rate_limited, max_turns, context_exhausted, wall_clock, or no
 * reported outcome) goes back to the item's owner: the item is blocked with a reported blocker that says what
 * happened, which wakes the owner (`blocker`, next to the `run_ended` wake), and the owner decides (retry_item,
 * unblock_item, revise or cancel). Stop reasons that need a person (auth, policy, permission, environment) escalate to
 * the human. Decision 0040 bounds the automatic continuations: a downgraded done claim and a lost runner each get one
 * more attempt after AUTO_RETRY_BACKOFF_MS; the second downgraded claim in a row hands back to the owner agent (Wave A
 * §4.7, no card). At Act, a second lost runner and stagnant work return to delegated diagnosis too. Explicit
 * project count budgets hold further attempts for a person; progressing work has no implicit attempt ceiling.
 */
export const transitionsAfterRunEnded = (
  tx: Tx,
  run: Run,
  stopReason: StopReason,
  accepted: RunOutcome,
  _fingerprint: string | null,
  opts: RunEndOptions = {},
): RetryDecision => {
  if (run.kind === "turn" || run.kind === "publish" || !run.itemId) return { _tag: "none" }
  recordNativeExecution(tx, run, stopReason, accepted)
  if (stopReason === "superseded") return { _tag: "none" }
  const item = itemOf(tx.state, run.itemId)
  if (!item) return { _tag: "none" }
  if (stopReason === "cancelled") {
    // CloseItem already moved the item; a cancelled run alone (CancelRun, a hub revoke) leaves the item to a
    // person: back to todo and paused, like an interrupt, so it never keeps an ended activeRunId.
    if (run.kind === "worker" && item.status === "in_progress" && item.activeRunId === run.id) {
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: item.id,
        from: "in_progress",
        to: "todo",
        cause: { _tag: "attempt_ended", runId: run.id, stopReason, outcome: accepted._tag, retry: null },
        blocked: null,
        resolution: null,
      })
      // K8: a worker cancelled because its project paused goes back to todo unpaused, so resuming dispatches it.
      // Decision 0041: a run cancelled because a gate was added (holdForGate) goes back unpaused too: the gate holds
      // it, and the answer that releases the gate dispatches it again.
      const held = heldByGate(tx.state, must(itemOf(tx.state, item.id), "item"))
      // decision 0116: a run stopped before it started because its item's place changed is dispatched again, unpaused
      const replaced = (runOf(tx.state, run.id) ?? run).cancelRedispatch === true
      if (tx.state.project?.status === "active" && !held && !replaced)
        tx.emit({ _tag: "ItemPaused", itemId: item.id, paused: true, by: stopRequester(tx, run) })
    }
    return { _tag: "none" }
  }
  if (run.kind === "validator") return validatorEnded(tx, run, item, stopReason, opts)
  if (item.status !== "in_progress" || item.activeRunId !== run.id) return { _tag: "none" }

  const ended = (
    to: Item["status"],
    blocked: Item["blocked"] = null,
    retry: RetryAt | null = null,
    progressed = false,
  ) =>
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId: item.id,
      from: "in_progress",
      to,
      cause: {
        _tag: "attempt_ended",
        runId: run.id,
        stopReason,
        outcome: accepted._tag,
        retry,
        ...(progressed ? { progressed: true as const } : {}),
      },
      blocked,
      resolution: null,
    })
  // escalations first move the item out of in_progress (attempt ended), then block it with the question
  const escalateAfter = (cause: EscalationCause, detail: string): RetryDecision => {
    ended("todo")
    escalate(tx, must(itemOf(tx.state, item.id), "item"), cause, detail, run.id)
    return { _tag: "escalate", cause }
  }
  // a failed run goes back to the owner, who decides (rev 2.1)
  const handBack = (what: string): RetryDecision => {
    ended("blocked", { _tag: "reported", blocker: { _tag: "other", what }, runId: run.id })
    return { _tag: "none" }
  }
  // one automatic continuation (decision 0040): todo with a pending retry, so readiness waits out the backoff
  const continueAt = (at: number, progressed = false): RetryDecision => {
    ended("todo", null, { at, pinProfile: null }, progressed)
    return { _tag: "new_attempt", notBefore: at, sameProfile: false }
  }

  // loop breakers (decision 0040). `lastRunId` is still the previous attempt here (this one has not ended yet); it
  // counts as "in a row" only when no person acted in between (n counts this attempt, so n ≥ 2 includes the previous)
  const n = item.unattendedAttempts ?? 0
  // Wave A §6: every attempt since a person last acted (this one included), progressed or not
  const auto = item.autoAttempts ?? 0
  const progressed = stopReason === "completed" && madeProgress(tx.state, runOf(tx.state, run.id) ?? run)
  const configuredLimit = workerBudgetReached(tx.state, item, progressed)
  // A blocked, question or impossible claim hands the item to its owner. An explicit budget can hold that round trip;
  // a valid done claim still goes to review, preserving the actual result.
  if (
    configuredLimit !== null &&
    (accepted._tag === "blocked" || accepted._tag === "question" || accepted._tag === "impossible")
  )
    return escalateAfter(
      "loop",
      `${configuredLimit} attempts since a person last looked; configured worker budget reached: ${run.ref} ${
        accepted._tag === "blocked"
          ? `reported blocked (${accepted.blocker._tag === "depends_on" ? `depends on ${itemOf(tx.state, accepted.blocker.itemId)?.ref ?? accepted.blocker.itemId}` : accepted.blocker.what})`
          : accepted._tag === "question"
            ? "asked a question"
            : `claimed it impossible (${accepted.reason}: ${accepted.explanation})`
      }`,
    )

  // claims that apply whatever the stop reason
  switch (accepted._tag) {
    case "done":
      ended("in_review")
      enterCurrentStage(tx, item.id)
      return { _tag: "none" }
    case "blocked":
      ended("blocked", { _tag: "reported", blocker: accepted.blocker, runId: run.id })
      return { _tag: "none" }
    case "question":
      ended("blocked", { _tag: "question", askId: accepted.askId })
      return { _tag: "none" }
    case "impossible":
      ended("blocked", { _tag: "impossible_claimed", runId: run.id, reason: accepted.reason })
      return { _tag: "none" }
    default:
      break
  }

  if (stopReason === "interrupted") {
    ended("todo")
    // decision 0041: a run stopped before it started because a gate was added (holdForGate; the runner reports a
    // revoked preparing run as interrupted) goes back unpaused: the gate holds it
    const self = runOf(tx.state, run.id) ?? run
    const gated =
      self.startedAt === null &&
      (self.cancelRedispatch === true || heldByGate(tx.state, must(itemOf(tx.state, item.id), "item")))
    if (!gated) tx.emit({ _tag: "ItemPaused", itemId: item.id, paused: true, by: stopRequester(tx, run) })
    return { _tag: "none" }
  }
  const prev = item.lastRunId && n >= 2 ? runOf(tx.state, item.lastRunId) : undefined
  const self = runOf(tx.state, run.id) ?? run
  // decision 0093: a run that stopped for a reason a retry alone won't fix (sandbox, auth, permission, policy, budget,
  // MCP) goes back to the item's agent with the failure's own words, never straight to a person: the agent retries,
  // re-routes to another runner or profile, or asks the person itself. Nothing retries on its own. Only an item no
  // agent answers for escalates to a person
  // Decision 0095: the place it ran is in the words, and an item no agent owns goes to the lead instead of a person.
  if (NON_RETRYABLE.has(stopReason)) {
    const detail = `${run.ref} stopped with ${stopReason} (${failureReason(self)})${self.place ? ` on ${placeText(self.place)}` : ""}. It is not retried on its own: retry_item (optionally on another runner or profile), or ask the person if only they can fix it.`
    return failureHandlerOf(tx.state, item)
      ? handBack(detail)
      : escalateAfter(stopReason === "auth_error" ? "auth" : "non_retryable", detail)
  }

  const downgrade = stopReason === "completed" && accepted._tag === "progress" ? self.downgrade : null
  const lastWords = (): string =>
    downgrade
      ? `done claim not accepted: ${downgrade.detail ?? downgrade.rule}`
      : stopReason !== "completed"
        ? `${run.ref} stopped: ${stopReasonWords(stopReason)}`
        : accepted._tag === "unreported"
          ? `${run.ref} ended without reporting an outcome`
          : accepted._tag === "progress"
            ? `${run.ref} made progress; next: ${accepted.next}`
            : `${run.ref} ended`
  // Measured progress avoids stagnation recovery. Explicit all-attempt budgets still include progressing work.
  if (configuredLimit !== null)
    return escalateAfter(
      "loop",
      `${configuredLimit} attempts since a person last looked${progressed ? ", the last ones made progress" : ""}; configured worker budget reached: ${lastWords()}`,
    )
  if (n >= UNATTENDED_ATTEMPT_CAP && !progressed) {
    const detail = `${n} attempts without progress: ${lastWords()}. Diagnose the result and change the approach before retrying.`
    const owner = ownerAgentOf(tx.state, item)
    return owner && levelOf(owner) === "act" ? handBack(detail) : escalateAfter("loop", detail)
  }
  if (downgrade) {
    // Wave A §4.7: a second failed done claim in a row goes back to the owner agent, with no card at Act. Below Act
    // (TRI-05, decision 0055 rule 7) the owner's wake turn may not retry, so the hand-back opens a card for the person
    if (prev?.downgrade) {
      const detail = `the done claim was not accepted twice: ${downgrade.detail ?? downgrade.rule}`
      const owner = ownerAgentOf(tx.state, item)
      return owner && levelOf(owner) !== "act"
        ? handBackWithCard(tx, item, run, stopReason, accepted._tag, "repeated_failure", detail)
        : handBack(detail)
    }
    return continueAt(tx.now + AUTO_RETRY_BACKOFF_MS)
  }
  if (stopReason === "runner_lost") {
    if (prev?.stopReason !== "runner_lost") return continueAt(tx.now + AUTO_RETRY_BACKOFF_MS)
    const runners = prev ? `${prev.ref}, ${run.ref}` : run.ref
    // Decision 0095 (owner's decision 1): the second loss in a row goes back to the item's agent (its owner, else the
    // lead) at every level; below Act its wake turn asks the person itself. A card only when no agent can take it
    if (failureHandlerOf(tx.state, item)) {
      const where = [prev?.place, self.place]
        .filter((p): p is NonNullable<Run["place"]> => p !== undefined)
        .map((p) => `${p.runner} · ${p.profileId ?? "?"}`)
      return handBack(
        `The runner went offline twice (${runners}${where.length > 0 ? ` on ${[...new Set(where)].join(", ")}` : ""}). Check available runners and retained artifacts before choosing a new attempt.`,
      )
    }
    return handBackWithCard(
      tx,
      item,
      run,
      stopReason,
      accepted._tag,
      "runner_unavailable",
      `the runner went offline twice (${runners}): it was asleep, closed or crashed. Retry when it is back online.`,
    )
  }
  // decision 0155: a worker the provider's usage limit stopped continues by itself at the known reset (from its branch,
  // like a lost runner), instead of waiting for someone to retry it. Without a known reset the owner decides as before
  if (stopReason === "rate_limited" && opts.resetsAt != null)
    return continueAt(Math.max(tx.now + AUTO_RETRY_BACKOFF_MS, opts.resetsAt + RATE_LIMIT_RESUME_SLACK_MS))
  if (stopReason !== "completed") return handBack(failedRunText(run, stopReason, opts))
  // completed without an outcome
  // decision 0070 (TRI-25): the blocker is shown to people, so it states the fact; the agent's next steps are in its
  // turn file's blocker line
  if (accepted._tag === "unreported") return handBack(`${run.ref} ended without saying whether it finished.`)
  // progress: the worker continues with its `next`. It counts towards the unattended cap (decision 0040) unless it made
  // progress (Wave A §6); past the cap of 5 attempts of any kind each continuation waits the usual backoff
  return continueAt(auto > UNATTENDED_ATTEMPT_CAP ? tx.now + AUTO_RETRY_BACKOFF_MS : tx.now, progressed)
}

/**
 * Is an item held by a gate right now: an open blocking question or a `blocks` predecessor that is not done (open or
 * cancelled, as readiness holds it; decision 0041)?
 */
export const heldByGate = (s: ProjectState, item: Item): boolean => {
  if (blockingProposals(s, item).length > 0) return true
  const deps = blockingPredecessors(s, item)
  return deps.on.length > 0 || deps.cancelled.length > 0
}

/**
 * A gate was added (a question that `blocks` items, or a `blocks` edge from an open item; decision 0041): every worker
 * run on those items or below them that has not started yet (requested, waiting, or leased and still preparing) is
 * cancelled, so the item goes back to `todo` and waits on the gate. Runs that already started are not stopped; they are
 * returned so the caller can say so ("stop_run R-7 to stop it now").
 */
export const holdForGate = (tx: Tx, itemIds: ReadonlyArray<ItemId>, why: string): ReadonlyArray<Run> => {
  const seen = new Set<string>()
  const running: Run[] = []
  for (const id of itemIds) {
    const top = itemOf(tx.state, id)
    if (!top) continue
    for (const x of [top, ...descendantsOf(tx.state, top.id)]) {
      if (seen.has(x.id)) continue
      seen.add(x.id)
      const cur = itemOf(tx.state, x.id)
      const run = cur?.activeRunId ? runOf(tx.state, cur.activeRunId) : undefined
      if (run?.kind !== "worker" || run.state._tag === "ended") continue
      const st = run.state
      const notStarted =
        st._tag === "requested" || st._tag === "waiting" || (st._tag === "leased" && st.phase === "preparing")
      if (notStarted) tx.emit({ _tag: "RunCancelRequested", runId: run.id, reason: why })
      else running.push(run)
    }
  }
  return running
}

/**
 * Decision 0116: an item's placement or route changed while its worker run waits to start (requested, waiting, or
 * leased and still preparing): that run was placed with the old one (P-138: a pin to jannik-mbp, then the queued retry
 * started on the Platform Runner). Stop it before it starts; it goes back to todo unpaused and the scheduler dispatches
 * the next attempt with the new placement. A run that already started keeps its place (the change holds for the next
 * attempt); it is returned so the caller can say so.
 */
export const replaceUnstarted = (tx: Tx, item: Item, why: string): Run | null => {
  const run = item.activeRunId ? runOf(tx.state, item.activeRunId) : undefined
  if (run?.kind !== "worker" || run.state._tag === "ended" || run.cancelRequestedBy !== undefined) return null
  const st = run.state
  const notStarted =
    st._tag === "requested" || st._tag === "waiting" || (st._tag === "leased" && st.phase === "preparing")
  if (!notStarted) return run
  tx.emit({ _tag: "RunCancelRequested", runId: run.id, reason: why, requestedAt: tx.now, redispatch: true })
  return null
}

/** Does picking this option release the items a question `blocks` (decision 0041)? */
export const releasesGate = (o: QuestionOption): boolean => o.releases ?? o.key === "approve"

/** Who asked to stop a run (RunCancelRequested's actor), for the stop's pause; null = the system or unknown. */
const stopRequester = (tx: Tx, run: Run) =>
  runOf(tx.state, run.id)?.cancelRequestedBy ?? run.cancelRequestedBy ?? null

/** A stop reason in words, for people and agents alike. */
/**
 * Decision 0150: error codes of a run whose runner lost its lease without anyone asking it to stop: the lease ran out
 * with no contact (`lease_lost`, from a runner's self-fence) or a Platform sandbox heard no tick until its fence
 * (`platform_tick_lost`). A person's or agent's stop is a revoke (`revoked`) or `cancelled`, never one of these.
 */
export const LEASE_LOSS_CODES: ReadonlySet<string> = new Set(["lease_lost", PLATFORM_TICK_LOST])

/**
 * Decision 0150: the stop reason a run's end is recorded with. A runner reports a lost lease as `interrupted` (runners
 * up to 0.9.23 and the Platform image's self-fence); that is an infrastructure loss, so it takes the `runner_lost`
 * path (automatic continuation, the item is not paused) instead of the pause a person's stop gets.
 */
export const lostRunnerStop = (
  stopReason: StopReason,
  error: { readonly code: string } | null | undefined,
  /**
   * Decision 0158 (10-23 review #8, W-171): the run as it is. A run a person or an agent asked to stop (`stop_run`, not
   * a re-placement) that then ends as a lost runner was stopped on purpose: it is recorded as the stop it was
   * (`interrupted` once started, `cancelled` before), so it is paused like any stop and never auto-retried as a loss.
   */
  run?: Pick<Run, "cancelRequestedBy" | "cancelRedispatch" | "startedAt">,
): StopReason => {
  const lost =
    stopReason === "runner_lost" ||
    (stopReason === "interrupted" && error != null && LEASE_LOSS_CODES.has(error.code))
  if (lost && run?.cancelRequestedBy != null && run.cancelRedispatch !== true)
    return run.startedAt === null ? "cancelled" : "interrupted"
  return lost ? "runner_lost" : stopReason
}

export const stopReasonWords = (stopReason: StopReason): string => {
  switch (stopReason) {
    case "runner_lost":
      return "the runner went offline (asleep, closed or crashed)"
    case "rate_limited":
      return "the model's rate limit was reached"
    case "max_turns":
      return "it used up its turns"
    case "context_exhausted":
      return "its context window ran out"
    case "wall_clock":
      return "it ran out of time"
    case "no_progress":
      return "it made no progress for a long time and the runner stopped it"
    case "cli_error":
      return "the CLI failed"
    default:
      return stopReason.replace(/_/g, " ")
  }
}

/**
 * Decision 0093: why a run failed, in the runner's words: its error, else its last milestone ("preparing the
 * workspace"), else that it gave none. Escalations and hand-backs carry it, so nobody has to open the run to learn it.
 */
export const failureReason = (
  run: Pick<Run, "error" | "lastMilestone"> & Partial<Pick<Run, "stopReason">>,
): string => {
  const cut = (x: string, n = 300) => (x.length > n ? `${x.slice(0, n - 1)}…` : x)
  const message = run.error?.message?.trim()
  if (message) return cut(message, 600)
  const last = run.lastMilestone?.text?.trim()
  // decision 0095: no message from the runner → Minga's inference from the stop reason, marked as such
  const inferred = run.stopReason ? inferredReason(run.stopReason) : null
  const step = last ? `last step: ${cut(last)}` : null
  if (inferred)
    return `${inferred} (inferred by Minga: the runner sent no error message${step ? `; ${step}` : ""})`
  return step ? `no error reported; ${step}` : "the runner reported no error message"
}

/**
 * Decision 0095: what a stop reason alone says about the failure, for a run whose runner sent no message. Null for
 * stops that are not failures.
 */
export const inferredReason = (stopReason: StopReason): string | null => {
  switch (stopReason) {
    case "sandbox_error":
      return "the workspace or sandbox could not be prepared on this runner"
    case "policy_refused":
      return "the runner's policy ceiling refused the run (permission, run kind, repo or CLI not allowed there)"
    case "auth_error":
      return "the CLI's login was refused (logged out, expired, or the account has no access)"
    case "permission_denied":
      return "a permission was refused: the profile's access mode or sandbox blocked a command, path or tool"
    case "mcp_unavailable":
      return "Minga's MCP server did not load in the CLI, so the run could not report"
    case "budget_exhausted":
      return "the spending budget for this work is used up"
    case "runner_lost":
    case "rate_limited":
    case "max_turns":
    case "context_exhausted":
    case "wall_clock":
    case "no_progress":
    case "cli_error":
      return stopReasonWords(stopReason)
    default:
      return null
  }
}

/** Decision 0095: a run's place in one line ("e2e-mac · claude-personal · claude 2.1.3 · opus · subscription · auto"). */
export const placeText = (place: NonNullable<Run["place"]>): string =>
  [
    place.runner,
    place.profileId,
    place.cli ? `${place.cli}${place.cliVersion ? ` ${place.cliVersion}` : ""}` : null,
    place.model,
    place.account,
    place.access ? `access ${place.access}` : null,
  ]
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .join(" · ")

/**
 * Decision 0095: who gets a failed run back: the item's owner agent, else (a person owns the item, or nobody) the
 * project's lead. Undefined only when the project has no live lead either: then a person decides.
 */
export const failureHandlerOf = (s: ProjectState, item: Pick<Item, "owner">) => {
  const owner = ownerAgentOf(s, item)
  if (owner && owner.status !== "retired") return owner
  const lead = leadOf(s)
  return lead && lead.status !== "retired" ? lead : undefined
}

/**
 * A validator that stopped before its verdict leaves its item blocked at the validator stage (rev 2.1): retrying it
 * re-runs the validator for the same round, not a worker. Decision 0095: a retry's route then replaces the validator
 * route.
 */
export const retriesValidator = (s: ProjectState, t: Item): boolean => {
  const b = t.blocked
  // decision 0101: an escalation card about a validator's stop (no agent could take it) names that run too
  const escalated =
    b?._tag === "needs_human" ? s.proposals.find((p) => p.id === b.proposalId)?.body : undefined
  const runId =
    b?._tag === "reported"
      ? b.runId
      : escalated?._tag === "question"
        ? (escalated.escalation?.runId ?? null)
        : null
  const blockedBy = runId ? runOf(s, runId) : undefined
  return (
    blockedBy?.kind === "validator" &&
    blockedBy.itemId === t.id &&
    t.review.stages[t.review.stage] === "validator" &&
    t.lastRunId !== null
  )
}

/** Decision 0095: stops whose hand-back goes to the lead when no agent owns the item (no person card). */
export const goesToAnAgent = (stopReason: StopReason | null | undefined): boolean =>
  stopReason !== null &&
  stopReason !== undefined &&
  (NON_RETRYABLE.has(stopReason) || stopReason === "runner_lost")

/** The hand-back text for a run that stopped short (rev 2.1: the owner decides what happens next). */
const failedRunText = (run: Run, stopReason: StopReason, opts: RunEndOptions): string => {
  const reset =
    stopReason === "rate_limited" && opts.resetsAt != null
      ? ` (the profile's quota resets at ${new Date(opts.resetsAt).toISOString()})`
      : ""
  // decision 0095: where it ran and the runner's own error, when it sent one
  const where = run.place ? ` on ${placeText(run.place)}` : ""
  const message = run.error?.message?.trim()
  const error = message ? `: ${message.length > 600 ? `${message.slice(0, 599)}…` : message}` : ""
  return `${run.ref} stopped${where}: ${stopReasonWords(stopReason)} (${stopReason})${reset}${error}. Nothing retries on its own: retry_item, unblock_item or cancel`
}

/** Decision 0147: the family a validator ran on, and whether it is its author's (no other family was available). */
const reviewFamily = (
  s: ProjectState,
  run: Run,
  crossModel: boolean,
): { readonly family?: AgentKind; readonly sameFamily?: true } => {
  const family = run.executor ? runFamilyOf(run) : null
  if (!family) return {}
  if (!crossModel) return { family }
  const subject = run.subjectRunId ? runOf(s, run.subjectRunId) : undefined
  const author = subject ? runFamilyOf(subject) : null
  return { family, ...(author === family ? { sameFamily: true as const } : {}) }
}

const validatorEnded = (
  tx: Tx,
  run: Run,
  item: Item,
  stopReason: StopReason,
  opts: RunEndOptions,
): RetryDecision => {
  if (item.status !== "in_review" || item.review.stages[item.review.stage] !== "validator")
    return { _tag: "none" }
  const verdict = runOf(tx.state, run.id)?.verdict ?? run.verdict
  // decision 0154 (review 10-23 #1, R-642): a verdict the validator filed before its run was lost (or stopped short)
  // is its verdict: it is recorded like a completed run's, never thrown away for a re-run
  if (verdict) {
    const v = verdict._tag
    tx.emit({
      _tag: "ReviewRecorded",
      itemId: item.id,
      round: item.review.round,
      stage: "validator",
      verdict: v,
      notes: v === "inconclusive" && verdict._tag === "inconclusive" ? verdict.why : "",
      // decision 0147: a pass with notes keeps its minor findings for the owner (it decides about follow-ups)
      findings:
        verdict._tag === "fail"
          ? verdict.findings.map((f) => `[${f.severity}] ${f.text}`)
          : verdict._tag === "pass"
            ? (verdict.notes ?? []).filter((n) => n.text.trim() !== "").map((n) => `[note] ${n.text.trim()}`)
            : [],
      runId: run.id,
      ...reviewFamily(tx.state, runOf(tx.state, run.id) ?? run, item.review.crossModel),
    })
    if (v === "pass") passStage(tx, item.id, "pass")
    else if (v === "fail") failStage(tx, item.id, "fail", run.id)
    else inconclusiveStage(tx, item.id, run.id, verdict._tag === "inconclusive" ? verdict.why : "")
    return { _tag: "none" }
  }
  // decision 0134 (lesson 4): a validator that ended without a verdict is re-run once on its own (the item stays at its
  // validator stage and the reactions dispatch a new validator); the second such end blocks for the item's agent
  if (stopReason === "completed" && !verdict) {
    const silent = tx.state.runs.filter(
      (r) =>
        r.kind === "validator" &&
        r.itemId === item.id &&
        r.subjectRunId === run.subjectRunId &&
        r.stopReason === "completed" &&
        !r.verdict,
    ).length
    if (silent <= 1) return { _tag: "none" }
  }
  // decision 0154 (review 10-23 #1): a validator whose runner was lost (or whose lease was lost, 0150) is re-run once
  // on its own, like a worker (0040): the item stays at its validator stage and the reactions dispatch a new validator.
  // The second loss in a row (the previous validator of the same subject was lost too) goes back to the item's agent
  const lostBefore =
    stopReason === "runner_lost" &&
    tx.state.runs
      .filter(
        (r) =>
          r.kind === "validator" &&
          r.itemId === item.id &&
          r.id !== run.id &&
          r.subjectRunId === run.subjectRunId &&
          isRunEnded(r),
      )
      .sort((a, b) => a.requestedAt - b.requestedAt)
      .at(-1)?.stopReason === "runner_lost"
  if (stopReason === "runner_lost" && !lostBefore) return { _tag: "none" }
  // mechanical failures don't count as a review round
  const selfRun = runOf(tx.state, run.id) ?? run
  const why = `${selfRun.place ? `on ${placeText(selfRun.place)}: ` : ""}${failureReason(selfRun)}`
  // decision 0093: a validator that stopped for such a reason goes back to the item's agent (it re-runs or re-routes
  // the validator, or asks the person), never straight to a person; only an item no agent answers for escalates.
  // Decision 0095: an item a person (or nobody) owns goes to the lead
  if (NON_RETRYABLE.has(stopReason) && failureHandlerOf(tx.state, item)) {
    tx.emit({
      _tag: "ItemStatusChanged",
      itemId: item.id,
      from: "in_review",
      to: "blocked",
      cause: { _tag: "blocked" },
      blocked: {
        _tag: "reported",
        blocker: {
          _tag: "other",
          what: `the validator ${run.ref} stopped with ${stopReason} (${why}): retry_item re-runs it (optionally with a validator route to another runner or profile), or ask the person if only they can fix it`,
        },
        runId: run.id,
      },
      resolution: null,
    })
    return { _tag: "none" }
  }
  if (NON_RETRYABLE.has(stopReason)) {
    escalate(
      tx,
      item,
      stopReason === "auth_error" ? "auth" : "non_retryable",
      `the validator ${run.ref} stopped with ${stopReason} (${why})`,
      run.id,
    )
    return { _tag: "escalate", cause: "non_retryable" }
  }
  // no automatic re-dispatch (rev 2.1): the item waits at its validator stage, blocked, until the owner decides.
  // retry_item re-runs the validator for the same round (decide/items.ts); unblock_item sends it back to a worker.
  const what =
    stopReason === "completed"
      ? `the validator ${run.ref} ended without a verdict: retry_item re-runs it, unblock_item returns the item to a worker`
      : lostBefore
        ? `the validator's runner went offline twice in a row (the second time ${run.ref}${selfRun.place ? ` on ${placeText(selfRun.place)}` : ""}). Check available runners: retry_item re-runs it (optionally with a validator route to another runner or profile), unblock_item returns the item to a worker`
        : `the validator ${failedRunText(run, stopReason, opts)}`
  tx.emit({
    _tag: "ItemStatusChanged",
    itemId: item.id,
    from: "in_review",
    to: "blocked",
    cause: { _tag: "blocked" },
    blocked: { _tag: "reported", blocker: { _tag: "other", what }, runId: run.id },
    resolution: null,
  })
  return { _tag: "none" }
}

/**
 * onRunEnded(state, run, stopReason, fingerprint, now): the pure outcome table. `fingerprint` is this run's error
 * fingerprint. The returned item events follow the RunEnded event.
 */
const recordNativeExecution = (tx: Tx, run: Run, stopReason: StopReason, accepted: RunOutcome): void => {
  if (accepted._tag !== "done" || stopReason !== "completed") return
  for (const evidence of accepted.evidence) {
    if (evidence._tag !== "execution" || !evidence.proposalId) continue
    const proposal = tx.state.proposals.find((p) => p.id === evidence.proposalId)
    if (
      proposal?.state !== "approved" ||
      proposal.revision !== evidence.revision ||
      proposal.body._tag !== "outward_action" ||
      proposal.body.itemId !== run.itemId
    )
      continue
    tx.emit({
      _tag: "OutwardActionExecuted",
      proposalId: proposal.id,
      revision: proposal.revision,
      runId: run.id,
      resultRef: evidence.resultRef,
    })
    tx.emit({
      _tag: "ProposalApplied",
      proposalId: proposal.id,
      revision: proposal.revision,
      changesetId: null,
    })
  }
}

export const onRunEnded = (
  state: ProjectState,
  run: Run,
  stopReason: StopReason,
  fingerprint: string | null,
  now: number,
  opts: RunEndOptions = {},
): OutcomeDecision => {
  const { accepted, downgrade } = acceptOutcome(state, run, stopReason)
  const ctx: DecideContext = {
    actor: { _tag: "system", process: "scheduler" },
    now,
    correlationId: `run:${run.id}`,
  }
  const tx = new Tx(state, ctx)
  tx.emit({
    _tag: "RunEnded",
    runId: run.id,
    epoch: run.epoch,
    stopReason,
    claimed: run.claimed,
    outcome: accepted,
    downgrade,
    verdict: run.verdict,
    usage: run.usage,
    error: run.error,
    sessionOut: run.sessionOut,
    transcriptRef: null,
  })
  const retry = transitionsAfterRunEnded(tx, run, stopReason, accepted, fingerprint, opts)
  return { accepted, downgrade, itemEvents: tx.events.slice(1), retry }
}
